import { baseApi } from "./baseApi";
import { PaginationArgs, ScopeType } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import {
  Company,
  CompanyResponse,
  CompanyVerification,
  CompanyVerificationResponse,
  Industry,
  Organization,
  OrganizationResponse,
  Province,
  Tag,
} from "types/companyTypes";

export const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // organization
    getOrganizations: builder.query<OrganizationResponse, PaginationArgs>({
      query: (args) => `/organization?${buildQueryParams(args || {})}`,
      providesTags: ["Organizations"],
    }),

    getOrganizationById: builder.query<Organization, number>({
      query: (id) => `/organization/${id}`,
      providesTags: ["Organizations"],
    }),

    createOrganization: builder.mutation<Organization, Partial<Organization>>({
      query: (newData) => ({
        url: "/organization",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Organizations"],
    }),

    updateOrganization: builder.mutation<
      Organization,
      { id: number; data: Partial<Organization> }
    >({
      query: ({ id, data }) => ({
        url: `/organization/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Organizations"],
    }),

    deleteOrganization: builder.mutation<void, number>({
      query: (id) => ({
        url: `/organization/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Organizations"],
    }),

    getUserOrganizations: builder.query<OrganizationResponse, PaginationArgs>({
      query: (args) => `/user/organization?${buildQueryParams(args || {})}`,
      providesTags: ["UserOrganizations"],
    }),

    // province
    getProvinces: builder.query<Province[], void>({
      query: () => "/province",
      providesTags: ["Provinces"],
      keepUnusedDataFor: 604800,
    }),

    // tag
    getTags: builder.query<Tag[], void>({
      query: () => "/tag",
      providesTags: ["Tags"],
      keepUnusedDataFor: 604800,
    }),

    // company
    getCompanies: builder.query<
      CompanyResponse,
      PaginationArgs & {
        scope?: ScopeType;
      }
    >({
      query: ({ scope, ...args }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: "/admin/company",
          organization: "/user/organization/company",
          user: "/user/company",
          responder: "",
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: `${path}?${buildQueryParams(args || {})}`,
        };
      },

      providesTags: ["Companies"],
    }),

    getCompany: builder.query<
      Company,
      {
        id: number;
        scope?: ScopeType;
      }
    >({
      query: ({ id, scope }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: `/admin/company/${id}`,
          organization: `/user/organization/company/${id}`,
          user: `/user/company/${id}`,
          responder: "",
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: path,
        };
      },

      providesTags: ["Companies"],
    }),

    createCompany: builder.mutation<
      Company,
      {
        body: Partial<Company>;
        scope?: ScopeType;
      }
    >({
      query: ({ body, scope }) => ({
        url: `/${scope}/company`,

        method: "POST",
        body,
      }),

      invalidatesTags: ["Companies"],
    }),

    updateCompany: builder.mutation<
      Company,
      {
        id: number;
        body: Partial<Company>;
        scope?: ScopeType;
      }
    >({
      query: ({ id, body, scope }) => ({
        url: `/${scope}/company/${id}`,

        method: "PUT",
        body,
      }),

      invalidatesTags: ["Companies"],
    }),

    deleteCompany: builder.mutation<
      void,
      {
        id: number;
        scope?: ScopeType;
      }
    >({
      query: ({ id, scope }) => ({
        url: `/${scope}/company/${id}`,

        method: "DELETE",
      }),

      invalidatesTags: ["Companies"],
    }),

    // company verification
    verifyCompany: builder.mutation<
      {
        message: string;
        data: {
          success: boolean;
        };
      },
      { nationalId: string; organizationId: number }
    >({
      query: (body) => ({
        url: "/company-verification/verify",
        method: "POST",
        body,
      }),
    }),

    getCompanyVerifications: builder.query<
      CompanyVerificationResponse,
      PaginationArgs
    >({
      query: (args) => `/company-verification?${buildQueryParams(args || {})}`,
      providesTags: ["CompanyVerification"],
      keepUnusedDataFor: 604800,
    }),

    createCompanyVerification: builder.mutation<
      CompanyVerification,
      { nationalIds: string[] }
    >({
      query: (newData) => ({
        url: "/company-verification",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["CompanyVerification"],
    }),
    deleteCompanyVerification: builder.mutation<void, number>({
      query: (id) => ({
        url: `/company-verification/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyVerification"],
    }),

    // industries
    getIndustries: builder.query<Industry[], void>({
      query: () => "/industry-sector",
      providesTags: ["Industries"],
      keepUnusedDataFor: 604800,
    }),
  }),
});

export const {
  // organization
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  // userOrganization
  useGetUserOrganizationsQuery,
  useLazyGetUserOrganizationsQuery,
  // province
  useGetProvincesQuery,
  // tag
  useGetTagsQuery,
  // company
  useGetCompaniesQuery,
  useLazyGetCompaniesQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  // company verification
  useVerifyCompanyMutation,
  useGetCompanyVerificationsQuery,
  useCreateCompanyVerificationMutation,
  useDeleteCompanyVerificationMutation,
  // industries
  useGetIndustriesQuery,
} = companyApi;
